import { FC } from 'react';
import { ClipboardIcon, TrashIcon } from '@heroicons/react/outline';
import { $convertToMarkdownString } from '@lexical/markdown';

import ActionButton from '../../components/ActionButton';
import ActionButtonGroup from '../../components/ActionButtonGroup';
import Button from '../../components/Button';
import InputGroup from '../../components/InputGroup';
import InputLabel from '../../components/InputLabel';
import Editor from '../../components/Editor';
import '../../components/Editor/index.css';
import { MARKDOWN_TRANSFORMERS } from '../../components/Editor/plugins/MarkdownTransformers';
import { TemplateStepDraft } from '../../lib/mailSequence';
import ScheduleMailOffsetControl from './ScheduleMailOffsetControl';
import { MAIL_SEQUENCE_FIELD_LABEL_CLASS } from './scheduleMailFieldLabel';
import Alert from '../../components/Alert';
import MailAlertMessage from '../../components/MailAlertMessage';
import type { MailTimingIssue } from './mailTimingValidation';

interface Props {
  stepIndex: number;
  step: TemplateStepDraft;
  scheduleSummary: string;
  timingIssues?: MailTimingIssue[];
  editorKey: string;
  onlyPatientFirstName?: string;
  onChange: (patch: Partial<TemplateStepDraft>) => void;
  onRemove: () => void;
  showRemove: boolean;
  onApplyTemplate?: () => void;
  sourceTemplateName?: string;
  footer?: React.ReactNode;
}

const SequenceStepEditor: FC<Props> = ({
  stepIndex,
  step,
  scheduleSummary,
  timingIssues = [],
  editorKey,
  onlyPatientFirstName,
  onChange,
  onRemove,
  showRemove,
  onApplyTemplate,
  sourceTemplateName,
  footer,
}) => (
  <div className="rounded-lg border border-neutral-75 bg-white p-5 shadow-100">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-bold text-white">
            {stepIndex + 1}
          </span>
          <span className="text-caption font-semibold text-neutral-150">
            Mail
          </span>
          <span className="text-caption text-neutral-110">|</span>
          <span className="text-caption text-neutral-150">
            Scheduled for {scheduleSummary}
          </span>
        </div>
        {sourceTemplateName ? (
          <p className="mt-1 text-small-caption text-neutral-110">
            From template: {sourceTemplateName}
          </p>
        ) : null}
      </div>
      {showRemove ? (
        <ActionButtonGroup size="small" className="shrink-0">
          <ActionButton
            size="small"
            onClick={onRemove}
            title={`Remove mail ${stepIndex + 1}`}
            aria-label={`Remove mail ${stepIndex + 1}`}
          >
            <TrashIcon className="h-4 w-4 text-red-125" />
          </ActionButton>
        </ActionButtonGroup>
      ) : null}
    </div>

    <div className="mb-4">
      <ScheduleMailOffsetControl
        offset={step.offsetDays}
        onChange={(offsetDays) => onChange({ offsetDays })}
      />
      {timingIssues.length > 0 ? (
        <div className="mt-3">
          <Alert
            level={
              timingIssues.some((issue) => issue.severity === 'error')
                ? 'error'
                : 'warning'
            }
            message="There are issues with this Mail:"
          >
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {timingIssues.map((issue, idx) => (
                <li key={`${issue.stepIndex}-${idx}`}>
                  <MailAlertMessage segments={issue.segments} />
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}
    </div>

    <div className="mb-4 flex items-start justify-between gap-4">
      <span className={`${MAIL_SEQUENCE_FIELD_LABEL_CLASS} mb-0`}>Content</span>
      {onApplyTemplate ? (
        <div className="flex h-6 shrink-0 items-center">
          <Button
            title="Apply Mail template"
            theme="secondary"
            size="small"
            IconComponent={ClipboardIcon}
            iconPosition="left"
            onClick={onApplyTemplate}
          />
        </div>
      ) : null}
    </div>

    <InputGroup
      label="Subject"
      placeholder={
        stepIndex === 0
          ? 'e.g. 1-month check-in'
          : `Subject for mail ${stepIndex + 1}`
      }
      useNaturalLettering
      value={step.subject}
      onChange={(e) => onChange({ subject: e.target.value })}
    />

    <div className="mt-4">
      <InputLabel label="Message" useNaturalLettering />
      <Editor
        elKey={editorKey}
        placeholder={`Hi ${
          onlyPatientFirstName ?? 'there'
        }, to follow up on our session...`}
        className="text-neutral-150"
        containerClassName="w-full"
        initialContentMarkdown={step.providerNote}
        onChange={(editorState) => {
          editorState.read(() => {
            onChange({
              providerNote: $convertToMarkdownString(
                MARKDOWN_TRANSFORMERS,
                undefined,
                true,
              ),
            });
          });
        }}
        onError={(error: Error) => {
          console.error(error);
        }}
      />
    </div>
    {footer}
  </div>
);

export default SequenceStepEditor;
